import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";

// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Divider,
  Button,
  ListItem,
  ListItemText,
  ListItemIcon,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import MDButton from "theme/components/MDButton";
// import MDInput from "theme/components/MDInput";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import axios from "axios.js";
import AppConfig from "appConfig";
import usePageTitle from "app/pageTitle";
import MDInput from "theme/components/MDInput";

import { useAppLocation } from "agent_location_context";
import { useAwaitDialog } from "app/components/status_dialog";
import DataFormComponent from "app/components/data_forms/data_form";
import PaymentInstrumentDialog from "app/components/payment_instrument_dialog";

export default function OpsEntityActivityPage() {
  const pageTitle = "Activity";
  usePageTitle(pageTitle);

  const { entityRef, activityRef } = useParams();
  const navigate = useNavigate();

  const statusDialog = useAwaitDialog();
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [pageLoaded, setPageLoaded] = useState(false);
  const [refreshPage, setRefreshPage] = useState(false);

  const dataFormRef = useRef();
  const [activity, setActivity] = useState(null);
  const { applocation, permissionStatus, updateAppLocation } = useAppLocation();
  const [paymentInstrument, setPaymentInstrument] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(
          `/agents/ops/entities/activity/${entityRef}/${activityRef}/fetch.json`,
        )
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            setActivity(result.data.data.activity);

            setPageLoaded(true);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg(
            `Unable to complete request. ${
              navigator.onLine
                ? ""
                : "Make sure you have an active internet connection"
            }`,
          );
          setOpen(true);
        });
    };

    fetchData();
  }, []);

  function submitActivity() {
    // Get form data
    let formValues = null;
    if (activity.data_form !== null) {
      const formResult = dataFormRef.current.submit();
      if (formResult.error) {
        setSeverity("error");
        setErrMsg(formResult.error);
        setOpen(true);
        return;
      }
      formValues = formResult.values;
    }

    if (!applocation) {
      setSeverity("error");
      setErrMsg(
        "GPS coords missing. Grant browser location permissions and try again.",
      );
      setOpen(true);
      return;
    }

    var payload = {
      entity: entityRef,
      activity: activityRef,
      lat: applocation?.lat,
      lng: applocation?.lng,
      data_form: formValues,
    };

    setShowLoading(true);
    axios
      .post("/agents/ops/entities/activity/submit.json", payload)
      .then(async function (result) {
        setShowLoading(false);
        if (result.data.status === "success") {
          if (result.data.data.status === "success") {
            await statusDialog.show(result.data.data.message, "success");
            navigate(`/ops/workspaces/entities/${entityRef}`);
          }

          if (result.data.data.status === "pending") {
            setPaymentInstrument(result.data.data);
          }
        } else {
          await statusDialog.show(result.data.data.message, "error", "Failed");
        }
      })
      .catch(async function () {
        setShowLoading(false);
        await statusDialog.show(
          "Unable to complete request.",
          "error",
          "Failed",
        );
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      {paymentInstrument && (
        <PaymentInstrumentDialog paymentDetails={paymentInstrument} />
      )}
      <MDBox mt={1} mb={5}>
        <MDBox ml={-2}>
          <Button
            // onClick={() => navigate(`/ops/workspaces/entities/${entityRef}`)}
            onClick={() => window.history.go(-1)}
            startIcon={<Icon>west</Icon>}
            color="dark"
          >
            Back
          </Button>
        </MDBox>
        {pageLoaded && (
          <MDBox mt={1}>
            <MDBox>
              <Card>
                <MDBox p={2}>
                  <MDBox>
                    <ListItem>
                      <ListItemText
                        primary={
                          <MDTypography variant="h6">
                            {activity.title}
                          </MDTypography>
                        }
                        secondary={
                          <MDTypography variant="caption">
                            {activity.entity.title}
                          </MDTypography>
                        }
                      />
                      <MDBox m={0}>
                        <MDTypography fontSize={32}>
                          <Icon>edit_note</Icon>
                        </MDTypography>
                      </MDBox>
                    </ListItem>
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
            <MDBox mt={1}>
              {activity.data_form && (
                <MDBox mt={3}>
                  <DataFormComponent
                    ref={dataFormRef}
                    dataForm={activity.data_form}
                  />
                </MDBox>
              )}
            </MDBox>
            <MDBox mt={5} mb={1}>
              <Card>
                <MDBox p={3}>
                  <MDButton
                    variant="contained"
                    color="primary"
                    onClick={submitActivity}
                    fullWidth
                  >
                    Submit
                  </MDButton>
                </MDBox>
              </Card>
            </MDBox>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
